import { MutationTree } from 'vuex'
import { Referral } from '@/api/model/referral'
import { ReferralsState, ReferralsStateFilters, SearchAlternatives } from '@/store/referrals'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import {
    PageResult,
    PipelinePhaseCategory,
    ReferralAge,
    ReferralCount,
    ReferralFilterDirection,
    ReferralStatus,
    ReferralView,
} from '@/api/types'
import { ReferralStats } from '@/api/model/referral-stats'
import { StatusReason } from '@/api/model/status-reason'
import { CompanyAPI } from '@/api/backend-client'
import { Source } from '@/api/model/source'

export const mutations: MutationTree<ReferralsState> = {
    fetchData(state: ReferralsState): void {
        state.loading = true
        state.totalItems = null
        state.error = null
    },

    fetchDataSuccess(state: ReferralsState, result: PageResult<Referral>): void {
        state.loading = false
        state.error = null
        state.referrals = result.collection
        state.totalItems = result.totalItems
        state.totalPages = result.totalPages
    },

    fetchDataFailure(state: ReferralsState, error: string): void {
        state.loading = false
        state.error = error
    },

    loadPipelines(state: ReferralsState): void {
        state.error = null
    },

    loadPipelinesSuccess(state: ReferralsState, pipelines: Pipeline[]): void {
        state.error = null
        state.pipelines = pipelines
            .filter(pipeline => pipeline.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    },

    loadOutgoingPipelinesSuccess(state: ReferralsState, pipelines: Pipeline[]): void {
        state.error = null
        state.outgoingPipelines = pipelines
            .filter(pipeline => pipeline.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    },

    loadPipelinesFailure(state: ReferralsState, error: string): void {
        state.error = error
    },

    loadingSearchAlternatives(state: ReferralsState): void {
        state.loadingAlternatives = true
    },

    loadingSearchAlternativesSuccess(state: ReferralsState, searchAlternatives: SearchAlternatives): void {
        state.loadingAlternatives = false
        state.searchAlternatives = searchAlternatives
    },

    loadingSearchAlternativesFailure(state: ReferralsState): void {
        state.loadingAlternatives = false
        state.searchAlternatives = {
            userIn: [],
            userOut: [],
            teamIn: [],
            teamOut: [],
        }
    },

    clearSearchAlternatives(state: ReferralsState): void {
        state.searchAlternatives = {
            userIn: [],
            userOut: [],
            teamIn: [],
            teamOut: [],
        }
    },

    setPage(state: ReferralsState, payload: { page: number }): void {
        state.page = payload.page
    },

    setItemsPerPage(state: ReferralsState, payload: { itemsPerPage: number }): void {
        state.itemsPerPage = payload.itemsPerPage
    },

    setDisplayMode(state: ReferralsState, payload: { displayMode: string }): void {
        state.displayMode = payload.displayMode
    },

    applyView(state: ReferralsState, payload: { view: ReferralView, direction: ReferralFilterDirection }): void {
        state.view = payload.view
        state.direction = payload.direction
    },

    applySearch(state: ReferralsState, payload: { query: string }): void {
        state.search = payload.query
    },

    applyOrder(state: ReferralsState, payload: { field: string, direction: string }): void {
        state.orderField = payload.field
        state.orderDirection = payload.direction
    },

    applyPriorityFilter(state: ReferralsState, payload: { priorityFilter: number[] }): void {
        state.priorityFilter = payload.priorityFilter
    },

    applyTypeFilter(state: ReferralsState, payload: { typeFilter: string[] }): void {
        state.typeFilter = payload.typeFilter
    },

    applyStatusFilter(state: ReferralsState, payload: { statusFilter: string }): void {
        state.statusFilter = payload.statusFilter
        state.phaseFilter = null
        state.phaseCategoryFilter = null
    },

    applyStatusReasonFilter(state: ReferralsState, payload: { statusReasonFilter: string[] }): void {
        state.statusReasonFilter = payload.statusReasonFilter
    },

    applyNewStatusReasonFilter(state: ReferralsState, payload: { newStatusReasonFilter: StatusReason[] }): void {
        state.newStatusReasonFilter = payload.newStatusReasonFilter
    },

    resetNewStatusReasonFilter(state: ReferralsState): void {
        state.newStatusReasonFilter = []
    },

    applyReferredByFilter(state: ReferralsState, payload: { referredByFilter: User[] }): void {
        state.referredByFilter = payload.referredByFilter
    },

    applyReferredByBranchFilter(state: ReferralsState, payload: { referredByBranchFilter: Branch[] }): void {
        state.referredByBranchFilter = payload.referredByBranchFilter
    },

    applyAssignedToFilter(state: ReferralsState, payload: { assignedToFilter: User[] }): void {
        state.assignedToFilter = payload.assignedToFilter
    },

    applyAssignedToBranchFilter(state: ReferralsState, payload: { assignedToBranchFilter: Branch[] }): void {
        state.assignedToBranchFilter = payload.assignedToBranchFilter
    },

    applyChannelFilter(state: ReferralsState, payload: { channelFilter: string[] }): void {
        state.channelFilter = payload.channelFilter
    },

    applyNewSourceFilter(state: ReferralsState, payload: { newSourceFilter: Source[] }): void {
        state.newSourceFilter = payload.newSourceFilter
    },

    applyScoreFilter(state: ReferralsState, payload: { scoreFilter: number[] }): void {
        state.scoreFilter = payload.scoreFilter
    },

    applyDateSpecificFilter(state: ReferralsState, payload: { day: Date }): void {
        state.dateSpecificFilter = payload.day
    },

    applyDateRangeFilter(state: ReferralsState, payload: { range: string }): void {
        state.dateRangeFilter = payload.range
    },

    applyAgeFilter(state: ReferralsState, payload: { ageFilter: ReferralAge }): void {
        state.ageFilter = payload.ageFilter
    },

    applyPipelineFilter(state: ReferralsState, payload: { id: string }): void {
        state.pipelineFilter = payload.id
    },

    applyPipelinePhaseFilter(state: ReferralsState, payload: { phase: PipelinePhase }): void {
        state.phaseFilter = payload.phase
        state.phaseCategoryFilter = null
        state.statusFilter = ReferralStatus.OPEN
    },

    applyPipelinePhaseCategoryFilter(state: ReferralsState, payload: { category: PipelinePhaseCategory }): void {
        state.phaseCategoryFilter = payload.category
        state.phaseFilter = null
        state.statusFilter = ReferralStatus.OPEN
    },

    applyFilters(state: ReferralsState, payload: ReferralsStateFilters): void {
        Object.keys(payload).forEach(key => {
            state[key] = payload[key]
        })
    },

    /**
     * Resets all filters to the store default.
     * Has no action and is ment to be used in combination with 'applyFilters'
     */
    clearFilters(state: ReferralsState): void {
        state.view = 'user'
        state.direction = 'in'
        state.search = null
        state.priorityFilter = []
        state.typeFilter = []
        state.statusFilter = null
        state.statusReasonFilter = []
        state.referredByFilter = []
        state.referredByBranchFilter = []
        state.assignedToFilter = []
        state.assignedToBranchFilter = []
        state.channelFilter = []
        state.newSourceFilter = []
        state.scoreFilter = []
        state.pipelineFilter = null
        state.phaseFilter = null
        state.phaseCategoryFilter = null
    },

    /**
     * Restore to the previously stored snapshot of filters.
     */
    resetFilters(state: ReferralsState): void {
        state.search = null
        state.priorityFilter = state.snapshot.priorityFilter
        state.typeFilter = state.snapshot.typeFilter
        state.statusReasonFilter = state.snapshot.statusReasonFilter
        state.referredByFilter = state.snapshot.referredByFilter
        state.referredByBranchFilter = state.snapshot.referredByBranchFilter
        state.assignedToFilter = state.snapshot.assignedToFilter
        state.assignedToBranchFilter = state.snapshot.assignedToBranchFilter
        state.channelFilter = state.snapshot.channelFilter
        state.newSourceFilter = state.snapshot.newSourceFilter
        state.scoreFilter = state.snapshot.scoreFilter
        state.dateRangeFilter = CompanyAPI.currentCompany.defaultDateRange
    },

    /**
     * Take a snapshot of the current filters.
     * These can be restored by comitting 'resetFilters'
     */
    snapshot(state: ReferralsState): void {
        state.snapshot.priorityFilter = state.priorityFilter
        state.snapshot.typeFilter = state.typeFilter
        state.snapshot.statusReasonFilter = state.statusReasonFilter
        state.snapshot.referredByFilter = state.referredByFilter
        state.snapshot.referredByBranchFilter = state.referredByBranchFilter
        state.snapshot.assignedToFilter = state.assignedToFilter
        state.snapshot.assignedToBranchFilter = state.assignedToBranchFilter
        state.snapshot.channelFilter = state.channelFilter
        state.snapshot.newSourceFilter = state.newSourceFilter
        state.snapshot.scoreFilter = state.scoreFilter
        state.snapshot.dateSpecificFilter = state.dateSpecificFilter
        state.snapshot.dateRangeFilter = state.dateRangeFilter
        state.snapshot.ageFilter = state.ageFilter
    },

    updateMenuCount(state: ReferralsState): void {
        state.referralCounts = {
            statusAll: 0,
            statusNew: 0,
            statusAppointment: 0,
            statusProposal: 0,
            statusOnHold: 0,
            statusPending: 0,
            statusUnreachable: 0,
            statusSuccessful: 0,
            statusProcessed: 0,
            statusClosed: 0,
            scorePositive: 0,
            scoreNeutral: 0,
            scoreNegative: 0,
            typeAppraisal: 0,
            typeEpa: 0,
            typeInformation: 0,
            typeMortgage: 0,
            typePurchase: 0,
            typePhoto: 0,
            typeSale: 0,
            typeSignPlacement: 0,
            typeTaxation: 0,
            typeTechnicalAppraisal: 0,
            typeNewBuild: 0,
            age90: 0,
            age180: 0,
            age365: 0,
        }
    },

    updateMenuCountSuccess(state: ReferralsState, payload: ReferralCount): void {
        state.referralCounts = payload
    },

    updateMenuCountFailure(state: ReferralsState, error: string): void {
        state.error = error
    },

    updateNewMenuCount(state: ReferralsState): void {
        state.referralStats = new ReferralStats()
    },

    updateNewMenuCountSuccess(state: ReferralsState, payload: ReferralStats): void {
        state.referralStats = payload
    },

    updateNewMenuCountFailure(state: ReferralsState, error: string): void {
        state.error = error
    },
}
